import { useCallback } from "react";
import { useKeyboardShortcuts } from "@/hooks/keyboard-shortcuts";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { KanbanDefaultStates } from "@/components/ProjectList/CanvasKanban";
import { useFocusStore } from "@/store/zustand/focusStore";
import { v4 as uuidv4 } from "uuid";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { Issue } from "@/types/issues";

interface IssueKeyboardShortcutsProps {
  stateToIssues: Record<string, any>;
  sortColumnIssues: (issues: Issue[]) => Issue[];
  issuesToShow: Issue[];
  selectedIssueIds: string[];
  focusedIssueId: string | null;
  viewType: "grid" | "list";
  isEnabled?: boolean; // Add ability to disable shortcuts when needed
}

// Global flag to temporarily disable mouse hover focus during keyboard operations
let isKeyboardOperationActive = false;

// Function to temporarily disable mouse hover focus
const setKeyboardOperationActive = (active: boolean) => {
  isKeyboardOperationActive = active;
  if (active) {
    // Clear the flag after a reasonable delay to allow keyboard operation to complete
    setTimeout(() => {
      isKeyboardOperationActive = false;
    }, 200); // 200ms should be enough for the keyboard operation + focus restoration
  }
};

// Export function so components can check if they should ignore mouse hover
export const shouldIgnoreMouseHover = () => isKeyboardOperationActive;

export const useIssueKeyboardShortcuts = ({
  sortColumnIssues,
  issuesToShow,
  selectedIssueIds,
  focusedIssueId,
  viewType,
  isEnabled = true,
}: IssueKeyboardShortcutsProps) => {
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  // Get the ordered list of column statuses for navigation
  const getColumnOrder = useCallback(() => {
    return Object.keys(KanbanDefaultStates);
  }, []);

  // Helper function to check if all issues have the same status
  const allIssuesHaveSameStatus = useCallback(
    (issueIds: string[]) => {
      if (issueIds.length <= 1) return true;

      const statuses = issueIds
        .map((id) => {
          const issue = issuesToShow.find((issue) => issue.canvas.id === id);
          if (!issue) return null;
          const prd = issue.featurePrd;
          return prd?.status || KanbanDefaultStates.BACKLOG;
        })
        .filter((status) => status !== null);

      // Check if all statuses are the same
      const uniqueStatuses = new Set(statuses);
      return uniqueStatuses.size <= 1;
    },
    [issuesToShow],
  );

  // Move issues up or down within their current column
  const moveIssuesVertically = useCallback(
    async (issueIds: string[], direction: "up" | "down") => {
      if (!supabaseClient || issueIds.length === 0) return;

      // Don't allow movement if selected issues are in different statuses
      if (!allIssuesHaveSameStatus(issueIds)) {
        console.log(
          "Cannot move issues: selected issues are in different status columns",
        );
        return;
      }

      try {
        const prdsToUpdate: any[] = [];
        const processedColumns = new Set<string>();

        for (const issueId of issueIds) {
          const issue = issuesToShow.find(
            (issue) => issue.canvas.id === issueId,
          );
          if (!issue) continue;

          const prd = issue.featurePrd;
          const currentStatus = prd?.status || KanbanDefaultStates.BACKLOG;

          // Only process each column once to avoid duplicate updates
          if (processedColumns.has(currentStatus)) continue;
          processedColumns.add(currentStatus);

          // Get all issues in the current column
          const currentColumnIssues = sortColumnIssues(
            issuesToShow.filter((issue) => {
              const canvasPrd = issue.featurePrd;
              return (
                (canvasPrd?.status || KanbanDefaultStates.BACKLOG) ===
                currentStatus
              );
            }),
          );

          // Find which issues in this column are being moved
          const movingIssuesInColumn = issueIds.filter((id) =>
            currentColumnIssues.some((canvas) => canvas.canvas.id === id),
          );

          if (movingIssuesInColumn.length === 0) continue;

          // Create a reordered list
          const reorderedIssues = [...currentColumnIssues];

          // Improved logic for handling multiple selected issues
          if (movingIssuesInColumn.length === 1) {
            // Single issue - use simple logic
            const movingIssueId = movingIssuesInColumn[0];
            const currentIndex = reorderedIssues.findIndex(
              (canvas) => canvas.canvas.id === movingIssueId,
            );

            if (currentIndex !== -1) {
              let newIndex: number;
              if (direction === "up") {
                newIndex = Math.max(0, currentIndex - 1);
              } else {
                newIndex = Math.min(
                  reorderedIssues.length - 1,
                  currentIndex + 1,
                );
              }

              // Only move if there's actual change
              if (newIndex !== currentIndex) {
                const [movedIssue] = reorderedIssues.splice(currentIndex, 1);
                reorderedIssues.splice(newIndex, 0, movedIssue);
              }
            }
          } else {
            // Multiple issues - move as a group while maintaining relative order
            const movingIssuesWithIndices = movingIssuesInColumn
              .map((id) => ({
                id,
                currentIndex: reorderedIssues.findIndex(
                  (canvas) => canvas.canvas.id === id,
                ),
                canvas: reorderedIssues.find(
                  (canvas) => canvas.canvas.id === id,
                ),
              }))
              .filter((item) => item.currentIndex !== -1 && item.canvas)
              .sort((a, b) => a.currentIndex - b.currentIndex);

            if (movingIssuesWithIndices.length === 0) continue;

            // Find the target position for the group
            let targetIndex: number;
            if (direction === "up") {
              // Move group up: target is one position above the topmost selected issue
              const topIndex = movingIssuesWithIndices[0].currentIndex;
              targetIndex = Math.max(0, topIndex - 1);
            } else {
              // Move group down: find the first non-selected item after the group
              const bottomIndex =
                movingIssuesWithIndices[movingIssuesWithIndices.length - 1]
                  .currentIndex;
              const selectedIds = new Set(movingIssuesInColumn);

              // Find the next non-selected item after the bottom of the group
              let nextNonSelectedIndex = bottomIndex + 1;
              while (
                nextNonSelectedIndex < reorderedIssues.length &&
                selectedIds.has(reorderedIssues[nextNonSelectedIndex].canvas.id)
              ) {
                nextNonSelectedIndex++;
              }

              if (nextNonSelectedIndex >= reorderedIssues.length) {
                // No non-selected items after the group, move to the end
                targetIndex =
                  reorderedIssues.length - movingIssuesWithIndices.length;
              } else {
                // Move the group to just after the next non-selected item
                // After removal, the next non-selected item will be at bottomIndex + 1 - groupSize
                // So we want to insert at bottomIndex + 2 - groupSize
                targetIndex =
                  nextNonSelectedIndex - movingIssuesWithIndices.length + 1;
              }
            }

            // Remove all moving issues from their current positions (in reverse order to maintain indices)
            const movingCanvases = movingIssuesWithIndices.map(
              (item) => item.canvas,
            );
            for (let i = movingIssuesWithIndices.length - 1; i >= 0; i--) {
              reorderedIssues.splice(
                movingIssuesWithIndices[i].currentIndex,
                1,
              );
            }

            // Insert all moving issues at the target position (maintaining their relative order)
            reorderedIssues.splice(
              targetIndex,
              0,
              ...movingCanvases.filter((canvas) => canvas !== undefined),
            );
          }

          // Update PRDs for all issues in this column with new ordering
          reorderedIssues.forEach((canvas, index) => {
            const existingPrd = canvas.featurePrd;
            if (existingPrd) {
              prdsToUpdate.push({
                id: existingPrd.id,
                canvas_id: canvas.canvas.id,
                order: index,
              });
            } else {
              prdsToUpdate.push({
                id: uuidv4().toString(),
                canvas_id: canvas.canvas.id,
                status: currentStatus,
                order: index,
              });
            }
          });
        }

        useIssuesStore
          .getState()
          .bulkUpdateIssueOrder(supabaseClient, prdsToUpdate);
      } catch (error) {
        console.error(`Error moving issues ${direction}:`, error);
      }
    },
    [supabaseClient, sortColumnIssues, allIssuesHaveSameStatus, issuesToShow],
  );

  // Move issues to adjacent column (left or right)
  const moveIssuesHorizontally = useCallback(
    async (issueIds: string[], direction: "left" | "right") => {
      if (!supabaseClient || issueIds.length === 0) return;

      // Don't allow movement if selected issues are in different statuses
      if (!allIssuesHaveSameStatus(issueIds)) return;

      try {
        const columnOrder = getColumnOrder();
        const prdsToUpdate: any[] = [];

        // First, determine the target status for all issues (they should all move to the same target)
        const firstIssue = issuesToShow.find(
          (issue) => issue.canvas.id === issueIds[0],
        );
        if (!firstIssue) return;

        const firstPrd = issuesToShow.find(
          (issue) => issue.canvas.id === issueIds[0],
        )?.featurePrd;
        const sourceStatus = firstPrd?.status || KanbanDefaultStates.BACKLOG;

        // Find target column
        const sourceColumnKey = Object.entries(KanbanDefaultStates).find(
          ([_, value]) => value === sourceStatus,
        )?.[0];

        if (!sourceColumnKey) return;

        const sourceColumnIndex = columnOrder.indexOf(sourceColumnKey);
        if (sourceColumnIndex === -1) return;

        let targetColumnIndex: number;
        if (direction === "left") {
          targetColumnIndex = Math.max(0, sourceColumnIndex - 1);
        } else {
          targetColumnIndex = Math.min(
            columnOrder.length - 1,
            sourceColumnIndex + 1,
          );
        }

        // Skip if no movement needed
        if (targetColumnIndex === sourceColumnIndex) return;

        const targetColumnKey = columnOrder[targetColumnIndex];
        const targetStatus = KanbanDefaultStates[targetColumnKey];

        // Get all issues in source column, sorted by order
        const sourceColumnIssues = sortColumnIssues(
          issuesToShow.filter((issue) => {
            const canvasPrd = issue.featurePrd;
            return (
              (canvasPrd?.status || KanbanDefaultStates.BACKLOG) ===
              sourceStatus
            );
          }),
        );

        // Get all issues currently in the target column, sorted by order
        const currentTargetColumnIssues = sortColumnIssues(
          issuesToShow.filter((issue) => {
            const canvasPrd = issue.featurePrd;
            return (
              (canvasPrd?.status || KanbanDefaultStates.BACKLOG) ===
              targetStatus
            );
          }),
        );

        // Filter to only the issues being moved, preserving their original order
        const movingIssuesInOrder = sourceColumnIssues
          .filter((issue) => issueIds.includes(issue.canvas.id))
          .map((issue) => issue.canvas.id);

        // Find the reference issue for positioning (focused issue if in selection, otherwise first issue)
        const referenceIssueId =
          focusedIssueId && movingIssuesInOrder.includes(focusedIssueId)
            ? focusedIssueId
            : movingIssuesInOrder[0];

        // Calculate the normalized index of the reference issue in the source column
        const referenceNormalizedIndex = sourceColumnIssues.findIndex(
          (canvas) => canvas.canvas.id === referenceIssueId,
        );

        if (referenceNormalizedIndex === -1) return;

        // Calculate where to insert in the target column based on normalized index
        const targetInsertionIndex = Math.min(
          referenceNormalizedIndex,
          currentTargetColumnIssues.length,
        );

        // Create the new target column order by inserting moved issues at the normalized position
        const newTargetColumnOrder = [...currentTargetColumnIssues];

        // Insert the moving issues at the target normalized index
        const movingCanvases = movingIssuesInOrder
          .map((id) => issuesToShow.find((issue) => issue.canvas.id === id))
          .filter((issue) => issue !== undefined);

        newTargetColumnOrder.splice(targetInsertionIndex, 0, ...movingCanvases);

        // Create updates for all issues in the target column with consecutive order values
        newTargetColumnOrder.forEach((issue, normalizedIndex) => {
          // Skip if this isn't one of the moving issues - we'll handle existing target issues separately
          if (!movingIssuesInOrder.includes(issue.canvas.id)) return;

          const prd = issue.featurePrd;
          if (prd) {
            prdsToUpdate.push({
              ...prd,
              status: targetStatus,
              order: normalizedIndex,
            });
          } else {
            prdsToUpdate.push({
              id: uuidv4().toString(),
              canvas_id: issue.canvas.id,
              status: targetStatus,
              order: normalizedIndex,
            });
          }
        });

        // Update order values for existing issues in target column that need reordering
        const existingTargetIssues = newTargetColumnOrder.filter(
          (issue) => !movingIssuesInOrder.includes(issue.canvas.id),
        );

        existingTargetIssues.forEach((issue, index) => {
          const prd = issue.featurePrd;
          const currentOrder = prd?.order ?? 0;

          // Find this canvas's position in the new order
          const newNormalizedIndex = newTargetColumnOrder.findIndex(
            (c) => c.canvas.id === issue.canvas.id,
          );

          // Only update if the order actually changed
          if (newNormalizedIndex !== currentOrder) {
            if (prd) {
              // Update existing PRD with partial data
              prdsToUpdate.push({
                canvas_id: issue.canvas.id,
                order: newNormalizedIndex,
              });
            } else {
              // Create new PRD with required fields
              prdsToUpdate.push({
                id: uuidv4().toString(),
                canvas_id: issue.canvas.id,
                status: targetStatus,
                order: newNormalizedIndex,
              });
            }
          }
        });

        useIssuesStore
          .getState()
          .bulkUpdateIssueOrder(supabaseClient, prdsToUpdate);
      } catch (error) {
        console.error(`Error moving issues ${direction}:`, error);
      }
    },
    [
      supabaseClient,
      sortColumnIssues,
      getColumnOrder,
      allIssuesHaveSameStatus,
      focusedIssueId,
      issuesToShow,
    ],
  );

  // Handle reordering keyboard shortcuts
  const handleReorderUp = useCallback(
    (e: any) => {
      if (e.preventDefault) e.preventDefault();
      else e.returnValue = false;

      // Disable mouse hover focus during keyboard operation
      setKeyboardOperationActive(true);

      // Get the issues to move (selected issues or focused issue)
      const issuesToMove =
        selectedIssueIds.length > 0
          ? selectedIssueIds
          : focusedIssueId
            ? [focusedIssueId]
            : [];

      if (issuesToMove.length === 0) return;

      const primaryIssue = focusedIssueId || selectedIssueIds[0];
      moveIssuesVertically(issuesToMove, "up");

      // Restore focus to the primary issue
      if (primaryIssue) {
        setTimeout(
          () => useFocusStore.getState().setFocusedIssue(primaryIssue),
          50,
        );
      }
    },
    [selectedIssueIds, focusedIssueId, moveIssuesVertically],
  );

  const handleReorderDown = useCallback(
    (e: any) => {
      if (e.preventDefault) e.preventDefault();
      else e.returnValue = false;

      // Disable mouse hover focus during keyboard operation
      setKeyboardOperationActive(true);

      // Get the issues to move (selected issues or focused issue)
      const issuesToMove =
        selectedIssueIds.length > 0
          ? selectedIssueIds
          : focusedIssueId
            ? [focusedIssueId]
            : [];

      if (issuesToMove.length === 0) return;

      const primaryIssue = focusedIssueId || selectedIssueIds[0];
      moveIssuesVertically(issuesToMove, "down");

      // Restore focus to the primary issue
      if (primaryIssue) {
        setTimeout(
          () => useFocusStore.getState().setFocusedIssue(primaryIssue),
          50,
        );
      }
    },
    [selectedIssueIds, focusedIssueId, moveIssuesVertically],
  );

  const handleMoveLeft = useCallback(
    (e: any) => {
      // Only allow horizontal movement in Kanban view
      if (viewType !== "grid") return;

      if (e.preventDefault) e.preventDefault();
      else e.returnValue = false;

      // Disable mouse hover focus during keyboard operation
      setKeyboardOperationActive(true);

      // Get the issues to move (selected issues or focused issue)
      const issuesToMove =
        selectedIssueIds.length > 0
          ? selectedIssueIds
          : focusedIssueId
            ? [focusedIssueId]
            : [];

      if (issuesToMove.length === 0) return;

      const primaryIssue = focusedIssueId || selectedIssueIds[0];
      moveIssuesHorizontally(issuesToMove, "left");

      // Restore focus to the primary issue
      if (primaryIssue) {
        setTimeout(
          () => useFocusStore.getState().setFocusedIssue(primaryIssue),
          50,
        );
      }
    },
    [viewType, selectedIssueIds, focusedIssueId, moveIssuesHorizontally],
  );

  const handleMoveRight = useCallback(
    (e: any) => {
      // Only allow horizontal movement in Kanban view
      if (viewType !== "grid") return;

      if (e.preventDefault) e.preventDefault();
      else e.returnValue = false;

      // Disable mouse hover focus during keyboard operation
      setKeyboardOperationActive(true);

      // Get the issues to move (selected issues or focused issue)
      const issuesToMove =
        selectedIssueIds.length > 0
          ? selectedIssueIds
          : focusedIssueId
            ? [focusedIssueId]
            : [];

      if (issuesToMove.length === 0) return;

      const primaryIssue = focusedIssueId || selectedIssueIds[0];
      moveIssuesHorizontally(issuesToMove, "right");

      // Restore focus to the primary issue
      if (primaryIssue) {
        setTimeout(
          () => useFocusStore.getState().setFocusedIssue(primaryIssue),
          50,
        );
      }
    },
    [viewType, selectedIssueIds, focusedIssueId, moveIssuesHorizontally],
  );

  // Set up keyboard shortcuts
  useKeyboardShortcuts({
    shortcuts: [
      { key: "alt+up", handler: handleReorderUp, description: "Move issue up" },
      { key: "alt+j", handler: handleReorderUp, description: "Move issue up" },
      {
        key: "alt+down",
        handler: handleReorderDown,
        description: "Move issue down",
      },
      {
        key: "alt+k",
        handler: handleReorderDown,
        description: "Move issue down",
      },
      {
        key: "alt+left",
        handler: handleMoveLeft,
        description: "Move issue left",
      },
      {
        key: "alt+right",
        handler: handleMoveRight,
        description: "Move issue right",
      },
    ],
    enabled: isEnabled,
  });

  return {
    moveIssuesVertically,
    moveIssuesHorizontally,
  };
};
